// Apolo IDE — CompareColumn (Sprint v4 Epic S1 WS S1.3).
//
// One column per model in the side-by-side view. Renders the response (or
// error), attribution header, latency/cost footer, and the "Use this"
// winner-pick action.

import { Check, AlertTriangle, Trophy } from 'lucide-react';
import { findModel } from '../../../lib/modelRegistry.js';

function fmtMs(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtUsd(c) {
  if (typeof c !== 'number' || !Number.isFinite(c)) return '$0';
  if (c < 0.001) return '< $0.001';
  return `$${c.toFixed(c < 0.01 ? 4 : 3)}`;
}

export default function CompareColumn({ result, isWinner, onPickWinner }) {
  const entry = findModel(result.model);
  const label = entry?.label || result.model;
  const failed = result.status === 'failed';
  return (
    <article
      data-testid="compare-column"
      data-model={result.model}
      aria-label={`Resposta do modelo ${label}`}
      className={`flex h-full min-w-[280px] flex-col rounded-lg border bg-apolo-surface1 ${
        isWinner
          ? 'border-apolo-accent shadow-[0_0_0_1px_var(--apolo-accent)]'
          : 'border-apolo-borderSubtle'
      }`}
    >
      <header className="flex items-center justify-between gap-2 border-b border-apolo-borderSubtle px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-apolo-textPrimary">{label}</span>
          {isWinner ? (
            <Trophy size={11} className="text-apolo-accent" aria-label="Vencedor" />
          ) : null}
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${
            failed
              ? 'bg-apolo-error/15 text-apolo-error'
              : 'bg-apolo-success/15 text-apolo-success'
          }`}
          aria-label={failed ? 'Falhou' : 'Concluído'}
        >
          {failed ? <AlertTriangle size={9} aria-hidden="true" /> : <Check size={9} aria-hidden="true" />}
          {failed ? 'falhou' : 'ok'}
        </span>
      </header>

      <div
        className="flex-1 overflow-y-auto px-3 py-2 text-sm text-apolo-textPrimary"
        data-testid="compare-column-body"
      >
        {failed ? (
          <pre className="whitespace-pre-wrap text-xs text-apolo-error">{String(result.error || 'Erro desconhecido')}</pre>
        ) : (
          <pre className="whitespace-pre-wrap font-sans">{String(result.response ?? '')}</pre>
        )}
      </div>

      <footer className="flex items-center justify-between gap-2 border-t border-apolo-borderSubtle px-3 py-2 text-[11px] text-apolo-textTertiary">
        <span aria-label={`Latência ${fmtMs(result.latencyMs)}`}>{fmtMs(result.latencyMs)}</span>
        <span aria-label={`Custo ${fmtUsd(result.costUsd)}`}>{fmtUsd(result.costUsd)}</span>
        {!failed ? (
          <button
            type="button"
            onClick={() => onPickWinner?.(result.subagentId || result.model)}
            aria-label={`Usar resposta do ${label}`}
            className={`rounded-md border px-2 py-1 text-[11px] transition ${
              isWinner
                ? 'border-apolo-accent text-apolo-accent'
                : 'border-apolo-borderSubtle text-apolo-textSecondary hover:bg-apolo-surface2'
            }`}
          >
            {isWinner ? 'Selecionado' : 'Usar este'}
          </button>
        ) : null}
      </footer>
    </article>
  );
}
